import { createContext, useContext, useEffect, useState, useCallback } from "react";

const InstallContext = createContext(null);

const DISMISS_KEY = "ncontro_pwa_install_dismissed_at";
const DISMISS_DAYS = 14;

function isIosDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOS || iPadOS;
}

function isStandaloneMode() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function wasDismissedRecently() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!ts) return false;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export function InstallProvider({ children }) {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [standalone, setStandalone] = useState(isStandaloneMode());
  const [ios] = useState(isIosDevice());

  // Global BIP capture — happens once regardless of prompt visibility.
  useEffect(() => {
    if (standalone) return;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Only auto-show if user hasn't recently dismissed.
      if (!wasDismissedRecently()) setVisible(true);
    };
    const onInstalled = () => {
      setStandalone(true);
      setDeferred(null);
      setVisible(false);
    };

    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari has no BIP — auto-show manual instructions once, if not dismissed.
    let iosTimer;
    if (ios && !wasDismissedRecently()) {
      iosTimer = setTimeout(() => setVisible(true), 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, [standalone, ios]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  }, []);

  const showPrompt = useCallback(() => {
    // Manual re-open bypasses the 14-day dismissal window.
    setVisible(true);
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return "unavailable";
    try {
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      return outcome;
    } catch {
      return "failed";
    } finally {
      setDeferred(null);
      setVisible(false);
    }
  }, [deferred]);

  // canInstall = Android/desktop with a captured BIP event OR iOS (which requires manual instructions).
  const canInstall = !standalone && (Boolean(deferred) || ios);

  const value = {
    visible,
    setVisible,
    dismiss,
    showPrompt,
    install,
    canInstall,
    isIos: ios,
    isStandalone: standalone,
    hasDeferred: Boolean(deferred),
  };

  return <InstallContext.Provider value={value}>{children}</InstallContext.Provider>;
}

export function useInstall() {
  const ctx = useContext(InstallContext);
  if (!ctx) {
    // Safe default so consumers can render even outside the provider (e.g. tests).
    return {
      visible: false,
      setVisible: () => {},
      dismiss: () => {},
      showPrompt: () => {},
      install: async () => "unavailable",
      canInstall: false,
      isIos: false,
      isStandalone: false,
      hasDeferred: false,
    };
  }
  return ctx;
}
